import PropTypes from 'prop-types';
import React from 'react';
import { reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { defineMessages, injectIntl, intlShape } from 'react-intl';
import core from '@bravissimolabs/website-core';
import ShopFinder from 'brastrap/transactional/shop-finder/ShopFinder';
import EditorialContainer from 'brastrap/editorial/editorial-container/EditorialContainer';
import ShopReviews from 'brastrap/promotional/shop-reviews/ShopReviews';
import Meta from 'shared/containers/MetaContainer';
import {
  apiFindShops,
  usePostCodeSuggestion,
} from '../../../actions/shop-finder';
import { FIND_SHOPS_FORM_ID } from '../../../constants/shops';
import { receivePageContainers } from '../../../actions/editorial';
import { receiveAllShops } from '../../../actions/shops';
import {
  receiveReviews,
  getShopReviews,
  receiveRating,
} from '../../../actions/shop-reviews';

export const modalId = 'shop-finder.modal';

const messages = defineMessages({
  description: {
    id: 'shops-landing-page.description',
    defaultMessage:
      'Find your closest Bravissimo shop for the best bra fitting experience and the biggest collection of supportive D cup and up bras and swimwear',
  },
});

const ShopFinderFormComponent = reduxForm({
  form: FIND_SHOPS_FORM_ID,
  fields: ['location'],
})(ShopFinder);

const ShopFinderForm = props => (
  <Meta
    hideCanonicalTag={props.slug === 'error'}
    title={props.pageTitle || props.title}
    description={props.intl.formatMessage(messages.description)}
  >
    <ShopFinderFormComponent
      {...props.shopFinder}
      title={props.title}
      onSubmit={props.onSubmit}
      onChoosePostCode={props.onChoosePostCode}
      pageTitle={props.pageTitle}
      pageDescription={props.pageDescription}
      slug={props.slug}
    />
    <EditorialContainer containers={props.headerContainers} />
    <ShopReviews
      reviews={props.reviews}
      onReadMoreClick={props.onReadMoreClick}
      showReadMore={props.showReadMore}
      rating={props.rating}
    />
    <EditorialContainer containers={props.footerContainers} />
  </Meta>
);

ShopFinderForm.populateStore = async store => {
  const editorialApi = core.content.editorial;
  const shopApi = core.shop.shop;

  const content = await editorialApi.getWrapperContainers('shops');
  store.dispatch(receivePageContainers(content));

  const shops = await shopApi.getShops();
  store.dispatch(receiveAllShops(shops));

  const { shopReviews: { limit } = {} } = store.getState() || {};
  const rating = await shopApi.getShopRating();
  store.dispatch(receiveRating(rating));
  const reviews = await shopApi.getShopReviews(limit);
  store.dispatch(receiveReviews(false, reviews, rating));
};

ShopFinderForm.propTypes = {
  title: PropTypes.string,
  pageTitle: PropTypes.string,
  pageDescription: PropTypes.string,
  slug: PropTypes.string,
  displayInModal: PropTypes.bool,
  shopFinder: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onReadMoreClick: PropTypes.func,
  onChoosePostCode: PropTypes.func.isRequired,
  reviews: PropTypes.arrayOf(PropTypes.object),
  showReadMore: PropTypes.bool,
  rating: PropTypes.object,
  intl: intlShape,
  headerContainers: PropTypes.array,
  footerContainers: PropTypes.array,
};

const mapStateToProps = state => {
  const initialValues = {};
  const {
    account: { addresses },
    content: { headerContainers, footerContainers },
    shopReviews: { reviews = [], limit, showReadMore, rating } = {},
  } = state;

  // If the user is logged in we should have a main address.
  // Add the customers main address as the default value for the location
  const mainAddress =
    addresses.length &&
    (addresses.find(address => address.mainAddress) || addresses[0]);

  if (mainAddress && mainAddress.postCode) {
    initialValues.location = mainAddress.postCode;
  }

  // Shops come from two data sources, locale and outlet.
  // We need both on this page so we mash them together here.
  // @shops TODO: find a nicer way of doing this.
  const shopFinder = { ...state.shopFinder };
  shopFinder.shops = shopFinder.shops.map(shop => ({
    ...shop,
    slug: (state.shops.all.find(({ b2id }) => b2id === shop.id) || {}).slug,
  }));

  return {
    shopFinder,
    initialValues,
    displayInModal: true,
    reviews,
    limit,
    rating,
    showReadMore,
    headerContainers,
    footerContainers,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  onChoosePostCode: postCode => dispatch(usePostCodeSuggestion(postCode)),
  onSubmit: ({ location }) => dispatch(apiFindShops(location, 0)),
  onReadMoreClick: () => dispatch(getShopReviews()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ShopFinderForm));
